import React from 'react';
import { Link } from 'react-router-dom';
import { Package, ArrowRight, Check } from 'lucide-react';

const EventItemTracker = ({ items }) => {
  if (!items || !items.length) return null;

  const activeItems = items.filter(i => i.enabled !== false);
  if (!activeItems.length) return null;

  return (
    <section className="space-y-4 mb-6" data-testid="event-item-tracker">
      {activeItems.map((item, idx) => {
        const { name, image, goal_qty = 0, sold_qty = 0, price, revenue_total, support_text, cta_text, cta_link, sold_out } = item;
        const progress = goal_qty > 0 ? Math.min((sold_qty / goal_qty) * 100, 100) : 0;
        const remaining = Math.max(goal_qty - sold_qty, 0);
        const isSoldOut = sold_out || (goal_qty > 0 && sold_qty >= goal_qty);
        const ctaHref = cta_link || '#';
        const isExternal = ctaHref.startsWith('http');

        return (
          <div key={idx} className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden" data-testid={`item-tracker-${idx}`}>
            <div className="flex flex-col sm:flex-row">
              {/* Item Image */}
              {image && (
                <div className="sm:w-32 h-28 sm:h-auto flex-shrink-0">
                  <img src={image} alt={name} className="w-full h-full object-cover" />
                </div>
              )}

              {/* Content */}
              <div className="flex-1 p-5">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Package className="w-4 h-4 text-[#BA1826]" />
                      <h3 className="font-heading text-sm font-bold text-[#0A0A0A]" data-testid={`item-name-${idx}`}>
                        {name || 'Item'}
                      </h3>
                      {isSoldOut && (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-red-100 text-red-600 uppercase">Sold Out</span>
                      )}
                    </div>
                    {support_text && <p className="text-xs text-[#4B5563]">{support_text}</p>}
                    {price > 0 && <p className="text-xs font-semibold text-[#BA1826] mt-1">${price} each</p>}
                  </div>
                  {!isSoldOut && cta_text && (
                    isExternal ? (
                      <a href={ctaHref} target="_blank" rel="noopener noreferrer"
                        className="btn-primary text-[10px] px-3 py-1.5 flex items-center gap-1 flex-shrink-0"
                        data-testid={`item-cta-${idx}`}>
                        {cta_text} <ArrowRight className="w-2.5 h-2.5" />
                      </a>
                    ) : (
                      <Link to={ctaHref}
                        className="btn-primary text-[10px] px-3 py-1.5 flex items-center gap-1 flex-shrink-0"
                        data-testid={`item-cta-${idx}`}>
                        {cta_text} <ArrowRight className="w-2.5 h-2.5" />
                      </Link>
                    )
                  )}
                </div>

                {/* Progress */}
                {goal_qty > 0 && (
                  <>
                    <div className="w-full bg-[#F1F5F9] rounded-full h-2 mb-2 overflow-hidden">
                      <div
                        className="h-2 rounded-full transition-all duration-700"
                        style={{
                          width: `${progress}%`,
                          background: isSoldOut
                            ? 'linear-gradient(90deg, #22C55E, #16A34A)'
                            : 'linear-gradient(90deg, #BA1826, #EF4444)',
                        }}
                      />
                    </div>
                    <div className="flex items-center justify-between text-[10px]">
                      <span className="font-semibold text-[#0A0A0A]" data-testid={`item-sold-${idx}`}>
                        {sold_qty} sold
                      </span>
                      {isSoldOut ? (
                        <span className="flex items-center gap-0.5 text-green-600 font-semibold">
                          <Check className="w-3 h-3" /> Complete
                        </span>
                      ) : (
                        <span className="text-[#9CA3AF]">{remaining} remaining &middot; Goal: {goal_qty}</span>
                      )}
                    </div>
                  </>
                )}

                {/* Revenue (if shown) */}
                {revenue_total > 0 && (
                  <p className="text-[10px] text-[#4B5563] mt-1">${revenue_total.toLocaleString()} revenue</p>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </section>
  );
};

export default EventItemTracker;
